import { Component, computed, input, output } from '@angular/core';

/**
 * Badge component for displaying status indicators, counts, and labels.
 * Supports an optional delete button (Figma: node 428-10214) that is disabled
 * when the badge is in use by one or more champions.
 * Figma: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=2619-158083
 */
@Component({
  selector: 'app-badge',
  standalone: false,
  templateUrl: './badge.component.html',
  styleUrls: ['./badge.component.scss']
})
export class BadgeComponent {
  /**
   * The text content displayed in the badge
   */
  label = input.required<string>();

  /**
   * The visual variant of the badge
   * - warning: Yellow background (default, for availability/caution)
   * - success: Green background (for positive status)
   * - error: Red background (for errors/critical status)
   * - info: Blue background (for informational status)
   * - date-range: Yellow background with pill shape (for date/time ranges)
   * - category: Light gray background (for category/classification labels)
   */
  variant = input<'warning' | 'success' | 'error' | 'info' | 'date-range' | 'category'>('warning');

  /**
   * Custom border radius for the badge
   * Allows overriding the default border radius
   * Example: '8px', '50px', '4px'
   */
  borderRadius = input<string>();

  /**
   * Whether to show the delete (trash) button on the badge.
   * Figma: node 428-10214
   */
  deletable = input<boolean>(false);

  /**
   * Number of champions currently using this badge.
   * When > 0, the delete button is grayed out and non-interactive
   * to prevent removing a badge that is in use.
   */
  championsCount = input<number>(0);

  /**
   * Emitted when the delete button is clicked (only fires when not disabled).
   */
  deleteClicked = output<void>();

  /** Delete is disabled when at least one champion is assigned to this badge */
  isDeleteDisabled = computed(() => this.championsCount() > 0);

  onDelete(): void {
    if (!this.isDeleteDisabled()) {
      this.deleteClicked.emit();
    }
  }
}
